import {useObservable} from "@vueuse/rxjs";
import {liveQuery} from "dexie";
import axios from "axios";
import {db} from "@src/services/db";
import {SessionStatus} from "@src/types/enums/SessionStatus";
import ChatSession from "@src/types/ChatSession";
import SessionService from "@src/services/session";
import {useChatSessionStore} from "@src/stores/chatSession";

export function useChatSessions() {

    function getSessionsByStatus(status: string) {
        return useObservable<ChatSession[]>(
            // @ts-ignore
            liveQuery(async () => {
                let collection;

                if (status !== SessionStatus.ACTIVE && status !== SessionStatus.HOLD) {
                    collection = db.sessions
                        .where('status')
                        .noneOf([SessionStatus.ACTIVE, SessionStatus.HOLD]);
                } else {
                    collection = db.sessions
                        .where('status')
                        .equals(status);
                }
                return collection.sortBy('updated_at');
            }),
            {initialValue: []}
        )
    }

    function getSessionById(id: number | string) {
        return useObservable<ChatSession | null>(
            // @ts-ignore
            liveQuery(async () => {
                return await db.sessions.get(Number(id)) ?? null;
            }),
            {initialValue: null}
        )
    }

    function getSessionsWithNewMessagesCount(status: string) {
        return useObservable<number>(
            // @ts-ignore
            liveQuery(async () => {
                const collection = await db.sessions
                    .where('status')
                    .equals(status)
                    .and(session => session.has_new_messages)

                return collection.count()
            }),
            { initialValue: 0 }
        )
    }

    function getBotSessionsWithNewMessagesCount() {
        return useObservable<number>(
            // @ts-ignore
            liveQuery(async () => {
                const collection = await db.sessions
                    .where('status')
                    .anyOf(['bot', 'gpt', 'gemini', 'paused', 'advanced'])
                    .and(session => session.has_new_messages)

                return collection.count()
            }),
            {initialValue: 0}
        )
    }

    function getSessionsArchiveWithNewMessagesCount() {
        return useObservable<number>(
            // @ts-ignore
            liveQuery(async () => {
                const collection = await db.sessions
                    .where('status')
                    .equals('active')
                    .and(session =>
                        session.has_new_messages &&
                        session.is_archive === true
                    );

                return collection.count()
            }),
            {initialValue: 0}
        )
    }

        async function moveSessionToKanban(
        sessionId: number,
        kanbanId: number
    ) {

        await axios.post(
            `/operator/v4/kanban/session-update`,
            {
                session: sessionId,
                kanban: kanbanId
            },
            {
                withCredentials: true
            }
        )

        await db.sessions.update(sessionId, {
            kanban_card: kanbanId,
            updated_at: Date.now().toString()
        })
    }

    async function pauseSession(session: number) {
        try {
            await axios.post(
                `/operator/v4/sessions/put-on-hold`,
                {
                    session: session
                },
                {
                    withCredentials: true
                }
            )

            await db.sessions.delete(session);

            return true

        } catch (error) {
            console.error('❌ Erro ao pausar sessão', error)
            return false
        }
    }

    async function finishSession(session: number) {
        try {
            await axios.post(
                `/operator/v4/sessions/finish`,
                {
                    session: session
                },
                {
                    withCredentials: true
                }
            )

            await db.sessions.delete(session);

            return true

        } catch (error) {
            console.error('❌ Erro ao finalizar sessão', error)
            return false
        }
    }

    async function archiveSession(session: number, status: boolean) {

        try {
            await axios.post(
                `/operator/v4/sessions/archive`,
                {
                    session: session,
                    status: status
                },
                {
                    withCredentials: true
                }
            )

            await db.sessions.update(session, {
                is_archive: status,
                updated_at: Date.now().toString()
            })
            return true

        } catch (error) {
            console.error('❌ Erro ao archive sessão', error)
            return false
        }
    }

    async function newSession(session: ChatSession) {
        await db.sessions.put(session)
    }

    async function sessionExistsByToken(token: string, type: string): Promise<boolean> {
        const session = await db.sessions
            .filter(s => s.costumer?.token === token && s.channel.type === type && s.status === SessionStatus.ACTIVE)
            .first();

        return !!session;
    }

    async function pinMessage(sessionId: number, messageId: number | string) {
        try {

            // 🔵 Busca a mensagem no IndexedDB (objeto puro)
            const message = await db.messages.get(messageId)

            if (!message) {
                console.error('Mensagem não encontrada no DB')
                return false
            }

            // 🔵 Salva na sessão
            await db.sessions.update(sessionId, {
                pinned_message: message,
                pinned_message_hidden: false,
                updated_at: Date.now().toString()
            })

            return true

        } catch (error) {
            console.error('❌ Erro ao fixar mensagem', error)
            return false
        }
    }


    async function unpinMessage(sessionId: number) {
        try {

            await axios.post(
                `/operator/v4/sessions/unpin-message`,
                {
                    session: sessionId
                },
                {
                    withCredentials: true
                }
            )

            await db.sessions.update(sessionId, {
                pinned_message: null,
                pinned_message_hidden: false,
                updated_at: Date.now().toString()
            })

            return true

        } catch (error) {
            console.error('❌ Erro ao remover mensagem fixada', error)
            return false
        }
    }

    async function toggleSessionFixed(session: ChatSession | { id: number; is_fixed?: boolean }) {
        const sessionId = Number(session.id);
        const sessionStore = useChatSessionStore();

        const current = await db.sessions.get(sessionId);
        const prevFixed = !!(current?.is_fixed ?? (session as any).is_fixed ?? false);
        const nextFixed = !prevFixed;

        // aplica estado no store também
        const applyStoreFixed = (val: boolean) => {
            // selectedSession
            if (sessionStore.selectedSession && Number((sessionStore.selectedSession as any).id) === sessionId) {
                (sessionStore.selectedSession as any).is_fixed = val;
            }
            // sessions list (se você usa isso em algum lugar)
            const idx = sessionStore.sessions.findIndex((s: any) => Number(s.id) === sessionId);
            if (idx !== -1) {
                (sessionStore.sessions[idx] as any).is_fixed = val;
            }
        };

        //  local
        try {
            await db.sessions.update(sessionId, {is_fixed: nextFixed, updated_at: Date.now()} as any);
            applyStoreFixed(nextFixed);
        } catch (e) {
            console.error("❌ Erro ao atualizar sessão no IndexedDB", e);
            return false;
        }

        // backend
        const res = await SessionService.setSessionFixed(sessionId, nextFixed);

        if (res === false) {
            // rollback
            try {
                await db.sessions.update(sessionId, {is_fixed: prevFixed, updated_at: Date.now()} as any);
            } catch {
            }
            applyStoreFixed(prevFixed);
            return false;
        }

        // sincronizado com retorno do backend
        const finalFixed = res.is_fixed;
        try {
            await db.sessions.update(sessionId, {is_fixed: finalFixed, updated_at: Date.now()} as any);
        } catch {
        }
        applyStoreFixed(finalFixed);

        return true;
    }

    async function toggleSessionHasNewMessage(session: ChatSession | { id: number; }) {
        const sessionId = Number(session.id);

        try {
            await db.sessions.update(sessionId, {has_new_messages: false, updated_at: Date.now()} as any);
        } catch (e) {
            console.error("❌ Erro ao atualizar sessão no IndexedDB", e);
            return false;
        }

        // backend
        await SessionService.setSessionHasNewMessage(sessionId);

        return true;
    }


    return {
        getSessionsByStatus,
        getSessionById,
        getSessionsWithNewMessagesCount,
        getBotSessionsWithNewMessagesCount,
        getSessionsArchiveWithNewMessagesCount,
        moveSessionToKanban,
        pauseSession,
        finishSession,
        archiveSession,
        newSession,
        pinMessage,
        unpinMessage,
        sessionExistsByToken,
        toggleSessionFixed,
        toggleSessionHasNewMessage
    }
}
